/* ─────────────────────────────────────────────────────────────────────
   Smoosh — Set Build - Reactive (V3)
   Vanilla JS reactive controller. Faithful port of D1 Sticky Dance
   prototype. One instance per section root on a page.
   ─────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  const ROOT_SELECTOR = '.sbr';
  const SWIPE_THRESHOLD = 40;

  /* Hardcoded fallback for the 4 prototype scrubber colors — kicks in when
     the section's color map is empty AND no metafield is set. Defined at
     module scope (not as a static class field) for older-browser safety. */
  const KNOWN_COLORS = {
    lavender: '#C9B4FF',
    sage:     '#C7D4B5',
    black:    '#2D2342',
    blue:     '#8FBFE8'
  };

  /* ─── Discount application (mirrors v2 pattern) ─────────────── */
  let _discountsApplied = '';

  async function applyDiscounts(codes) {
    const joined = (codes || []).filter(Boolean).join(',');
    if (_discountsApplied === joined) return;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ discount: joined })
      });
      _discountsApplied = joined;
    } catch (e) { console.warn('[sbr] discount apply failed', e); }
  }

  async function clearDiscounts() {
    if (!_discountsApplied) return;
    _discountsApplied = '';
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ discount: '' })
      });
    } catch (e) { console.warn('[sbr] discount clear failed', e); }
  }

  /* ─── SetBuilder controller ─────────────────────────────────── */
  class SetBuilder {
    constructor(root) {
      this.root = root;
      this.sectionId = root.id.replace(/^sbr-/, '');
      const data = (window.SBR_DATA || {})[this.sectionId];
      if (!data) { console.warn('[sbr] no data for section', this.sectionId); return; }
      this.data = data;
      this.colorMap = this.parseColorMap(data.colorMapRaw || '');

      // Pre-select first available scrubber variant (prototype defaults to lavender;
      // production picks the first non-disabled variant).
      const firstAvail = (data.scrubber.variants || []).find(v => v.available)
        || data.scrubber.variants[0]
        || null;

      // slotOrder: positional array of variantId|null, length = requiredSoaps.
      // Same scent in multiple slots is allowed (means user wants 2x of it).
      const requiredSoaps = (data.settings && data.settings.requiredSoaps) || 2;
      this.state = {
        scrubberVariantId: firstAvail ? String(firstAvail.id) : null,
        slotOrder: new Array(requiredSoaps).fill(null),
        plan: 'subscribe',
        carouselIdx: 0,
        atcVisible: false,
        submitting: false,
        openSlot: null  // index of slot whose bucket-sheet is open (null = closed)
      };

      this.scentLayout = root.getAttribute('data-scent-layout') || 'hscroll';

      // DOM refs
      this.refs = {
        rh:          root.querySelector('[data-sbr-rh]'),
        compositor:  root.querySelector('[data-sbr-compositor]'),
        layerLeft:     root.querySelector('[data-layer="left"]'),
        layerRight:    root.querySelector('[data-layer="right"]'),
        layerScrubber: root.querySelector('[data-layer="scrubber"]'),
        pip:           root.querySelector('[data-sbr-pip]'),
        pipInner:      root.querySelector('.cr-pip-inner'),
        pipLayerLeft:     root.querySelector('[data-pip-layer="left"]'),
        pipLayerRight:    root.querySelector('[data-pip-layer="right"]'),
        pipLayerScrubber: root.querySelector('[data-pip-layer="scrubber"]'),
        carousel:    root.querySelector('[data-sbr-carousel]'),
        track:       root.querySelector('[data-sbr-track]'),
        dots:        root.querySelector('[data-sbr-dots]'),
        thumbs:      root.querySelector('[data-sbr-thumbs]'),
        thumbsArrowPrev: root.querySelector('[data-sbr-thumbs-arrow="prev"]'),
        thumbsArrowNext: root.querySelector('[data-sbr-thumbs-arrow="next"]'),
        arrowPrev:   root.querySelector('[data-sbr-arrow="prev"]'),
        arrowNext:   root.querySelector('[data-sbr-arrow="next"]'),
        colorRow:    root.querySelector('[data-sbr-color-row]'),
        colorName:   root.querySelector('[data-sbr-color-name]'),
        scentGrid:   root.querySelector('[data-sbr-scent-grid]'),
        scentNotes:  root.querySelector('[data-sbr-scent-notes]'),
        counter:     root.querySelector('[data-sbr-counter]'),

        bucketRoot:  root.querySelector('[data-sbr-bucket]'),
        bucketSheet: root.querySelector('[data-sbr-bucket-sheet]'),
        bucketPanel: root.querySelector('[data-sbr-bucket-panel]'),
        bucketBackdrop: root.querySelector('[data-sbr-bucket-backdrop]'),
        bucketHandle:root.querySelector('[data-sbr-bucket-handle]'),
        bucketTitle: root.querySelector('[data-sbr-bucket-title]'),
        bucketClose: root.querySelector('[data-sbr-bucket-close]'),
        plan:        root.querySelector('[data-sbr-plan]'),
        planDetailSub:  root.querySelector('[data-sbr-plan-detail-sub]'),
        planDetailOnce: root.querySelector('[data-sbr-plan-detail-once]'),
        priceNow:    root.querySelector('[data-sbr-price-now]'),
        priceStrike: root.querySelector('[data-sbr-price-strike]'),
        priceNote:   root.querySelector('[data-sbr-price-note]'),
        stickySection: root.querySelector('.sbr-sticky-section'),
        atcWrap:     root.querySelector('[data-sbr-atc-wrap]'),
        atcCta:      root.querySelector('[data-sbr-atc-cta]'),
        atcLabel:    root.querySelector('[data-sbr-atc-label]'),
        atcPriceNow: root.querySelector('[data-sbr-atc-price-now]'),
        atcPriceStrike: root.querySelector('[data-sbr-atc-price-strike]'),
        atcCaption:  root.querySelector('[data-sbr-atc-caption]')
      };

      this.panelCount = this.refs.track ? this.refs.track.querySelectorAll('.cr-panel').length : 1;

      // Dev diagnostic: surface the raw variant data so anyone can see exactly
      // what option1 / title strings are coming through. Gate behind window.SBR_DEBUG.
      if (window.SBR_DEBUG) {
        console.log('[sbr] scrubber variants:', this.data.scrubber.variants);
        console.log('[sbr] color map:', Object.fromEntries(this.colorMap));
      }

      this.bindEvents();
      this.buildDots();
      this.buildThumbs();
      this.applySwatchColors();
      this.observeSection();
      this.render();
    }

    /* Parse "name=#hex" lines into a Map keyed by lowercase name. */
    parseColorMap(raw) {
      const map = new Map();
      if (!raw) return map;
      raw.split(/\r?\n/).forEach(line => {
        const trimmed = line.trim();
        if (!trimmed) return;
        const eq = trimmed.indexOf('=');
        if (eq <= 0) return;
        const name = trimmed.slice(0, eq).trim().toLowerCase();
        const hex  = trimmed.slice(eq + 1).trim();
        if (name && hex) map.set(name, hex);
      });
      return map;
    }

    /* Resolve a scrubber variant's hex.
       The variant's option1 (the Color option value) is the source of truth.
       Look it up handleized in the CMS color map, then in known-color defaults.
       Prototype-known names (lavender/sage/black/blue) are hardcoded so the
       default works without any setup. */
    resolveSwatchHex(variant) {
      if (!variant) return '#C9B4FF';
      if (variant.hex) return variant.hex;

      // option1 is handleized server-side ("Soft Lavender" → "soft-lavender")
      const key = (variant.option1 || variant.handle || '').toLowerCase().trim();
      if (!key) return '#C9B4FF';

      if (this.colorMap.has(key))                return this.colorMap.get(key);
      if (KNOWN_COLORS[key])          return KNOWN_COLORS[key];

      // Fall back to first word for compound names (e.g. "soft-lavender" → "lavender")
      const firstWord = key.split(/[-_]/)[0];
      if (this.colorMap.has(firstWord))          return this.colorMap.get(firstWord);
      if (KNOWN_COLORS[firstWord])    return KNOWN_COLORS[firstWord];

      console.warn(`[sbr] no color match for variant "${variant.title}" (option1="${variant.option1}"). Add a line "${key}=#hex" to the section's color map setting.`);
      return '#C9B4FF';
    }

    /* Set --swatch-hex on each rendered swatch button so the gradient backdrop
       reflects the right color before user interaction. */
    applySwatchColors() {
      this.refs.colorRow?.querySelectorAll('.swatch').forEach(btn => {
        const id = btn.getAttribute('data-variant-id');
        const v = this.findScrubberVariant(id);
        const hex = this.resolveSwatchHex(v);
        btn.style.setProperty('--swatch-hex', hex);
      });
    }

    /* ─── Bind events ───────────────────────────────────────── */
    bindEvents() {
      // Scrubber color
      this.refs.colorRow?.addEventListener('click', (e) => {
        const btn = e.target.closest('.swatch');
        if (!btn || btn.disabled) return;
        this.setScrubber(btn.getAttribute('data-variant-id'));
      });

      // Scent cards
      this.refs.scentGrid?.addEventListener('click', (e) => {
        const stepperBtn = e.target.closest('.stepper button');
        const card = e.target.closest('.scent-card-lg');
        if (!card) return;

        const variantId = card.getAttribute('data-variant-id');

        if (stepperBtn) {
          e.stopPropagation();
          const act = stepperBtn.getAttribute('data-act');
          if (act === 'inc') this.incScent(variantId);
          else if (act === 'dec') this.decScent(variantId);
          return;
        }

        // Card click (not on stepper) — add if not already on
        const isOn = this.countOf(variantId) > 0;
        if (!isOn && !card.classList.contains('is-disabled')) {
          this.incScent(variantId);
        }
      });

      // Plan picker (handles all 3 layouts)
      this.refs.plan?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-plan]');
        if (!btn) return;
        this.setPlan(btn.getAttribute('data-plan'));
      });

      // Carousel
      this.bindCarousel();
      this.refs.arrowPrev?.addEventListener('click', () => this.setCarouselIdx(this.state.carouselIdx - 1));
      this.refs.arrowNext?.addEventListener('click', () => this.setCarouselIdx(this.state.carouselIdx + 1));
      this.refs.pip?.addEventListener('click', () => this.setCarouselIdx(0));

      // ATC
      this.refs.atcCta?.addEventListener('click', () => this.handleAtc());

      // Bucket scent picker
      this.bindBucket();
    }

    bindBucket() {
      // Slot tap → open bucket sheet for that slot
      this.refs.bucketRoot?.addEventListener('click', (e) => {
        const slotEl = e.target.closest('[data-sbr-slot]');
        if (!slotEl) return;
        const idx = parseInt(slotEl.getAttribute('data-slot'), 10);
        this.openBucket(idx);
      });

      // Sheet — scent select → fill slot, close.
      // Any click outside the panel (backdrop, sheet padding, etc.) closes too.
      this.refs.bucketSheet?.addEventListener('click', (e) => {
        if (e.target.closest('[data-sbr-bucket-close]') || !e.target.closest('[data-sbr-bucket-panel]')) {
          this.closeBucket();
          return;
        }
        const scentBtn = e.target.closest('[data-sbr-bucket-scent]');
        if (scentBtn) {
          const vid = scentBtn.getAttribute('data-variant-id');
          if (this.state.openSlot != null) {
            this.setSlot(this.state.openSlot, vid);
          }
          this.closeBucket();
        }
      });

      // Drag-down to dismiss
      this.bindBucketDrag();

      // Esc to close — store ref so we can remove on section unload
      this._escHandler = (e) => {
        if (e.key === 'Escape' && this.state.openSlot != null) this.closeBucket();
      };
      document.addEventListener('keydown', this._escHandler);
    }

    bindBucketDrag() {
      const handle = this.refs.bucketHandle;
      const panel  = this.refs.bucketPanel;
      const sheet  = this.refs.bucketSheet;
      if (!handle || !panel || !sheet) return;

      let startY = 0;
      let dragging = false;

      const onStart = (e) => {
        startY = e.touches ? e.touches[0].clientY : e.clientY;
        dragging = true;
        sheet.classList.add('is-dragging');
      };
      const onMove = (e) => {
        if (!dragging) return;
        const y = e.touches ? e.touches[0].clientY : e.clientY;
        const dy = y - startY;
        // Only allow drag down (positive dy)
        const drag = Math.max(0, dy);
        panel.style.transform = `translateY(${drag}px)`;
      };
      const onEnd = (e) => {
        if (!dragging) return;
        dragging = false;
        sheet.classList.remove('is-dragging');
        const y = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
        const dy = y - startY;
        panel.style.transform = '';
        // Threshold: drag down 80px → close
        if (dy > 80) this.closeBucket();
      };

      handle.addEventListener('touchstart', onStart, { passive: true });
      handle.addEventListener('touchmove',  onMove,  { passive: true });
      handle.addEventListener('touchend',   onEnd);
      handle.addEventListener('mousedown',  onStart);
      window.addEventListener('mousemove',  onMove);
      window.addEventListener('mouseup',    onEnd);
      // Store refs so we can detach on unload (handle's listeners die with the node)
      this._dragOnMove = onMove;
      this._dragOnEnd  = onEnd;
    }

    destroy() {
      if (this._escHandler) {
        document.removeEventListener('keydown', this._escHandler);
        this._escHandler = null;
      }
      if (this._dragOnMove) {
        window.removeEventListener('mousemove', this._dragOnMove);
        this._dragOnMove = null;
      }
      if (this._dragOnEnd) {
        window.removeEventListener('mouseup', this._dragOnEnd);
        this._dragOnEnd = null;
      }
    }

    openBucket(slotIdx) {
      if (!this.refs.bucketSheet) return;
      this.state.openSlot = slotIdx;
      this.refs.bucketSheet.hidden = false;
      // Force reflow before adding class for transition
      void this.refs.bucketSheet.offsetWidth;
      this.refs.bucketSheet.classList.add('is-open');
      this.refs.bucketSheet.setAttribute('aria-hidden', 'false');

      // Update title (replace {n} with 1-indexed slot number)
      if (this.refs.bucketTitle) {
        const tpl = this.refs.bucketTitle.textContent.replace(/\d+/, String(slotIdx + 1));
        // Try template-style replacement if {n} marker present in original
        const original = this.refs.bucketTitle.dataset.template;
        if (original) {
          this.refs.bucketTitle.textContent = original.replace('{n}', String(slotIdx + 1));
        } else {
          // Cache template on first open
          this.refs.bucketTitle.dataset.template = this.refs.bucketTitle.textContent.replace(/\d+/, '{n}');
          this.refs.bucketTitle.textContent = this.refs.bucketTitle.dataset.template.replace('{n}', String(slotIdx + 1));
        }
      }

      this.renderBucket();  // mark current scent
      this.lockBodyScroll();
    }

    closeBucket() {
      if (!this.refs.bucketSheet) return;
      this.state.openSlot = null;
      this.refs.bucketSheet.classList.remove('is-open');
      this.refs.bucketSheet.setAttribute('aria-hidden', 'true');
      this.unlockBodyScroll();
      // Hide after transition
      setTimeout(() => {
        if (!this.refs.bucketSheet.classList.contains('is-open')) {
          this.refs.bucketSheet.hidden = true;
        }
      }, 320);
    }

    /* Background scroll lock. Pinning body with position:fixed (the
       textbook iOS-safe pattern) caused a visible scroll-jolt on
       close because removing the fix unsticks the body at scrollY=0
       before scrollTo() can restore the captured position.
       Lighter alternative: overflow:hidden on body + a touchmove
       listener that preventDefaults gestures outside the panel.
       Touches inside the panel (grid, handle) still propagate
       normally, so card scrolling and drag-down dismiss still work.
       overscroll-behavior:contain on the grid is the secondary
       safety net for momentum scroll. */
    lockBodyScroll() {
      if (this._scrollLocked) return;
      this._prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this._touchHandler = (e) => {
        if (!e.target.closest('[data-sbr-bucket-panel]')) {
          e.preventDefault();
        }
      };
      document.addEventListener('touchmove', this._touchHandler, { passive: false });
      this._scrollLocked = true;
    }
    unlockBodyScroll() {
      if (!this._scrollLocked) return;
      document.body.style.overflow = this._prevBodyOverflow || '';
      if (this._touchHandler) {
        document.removeEventListener('touchmove', this._touchHandler);
        this._touchHandler = null;
      }
      this._prevBodyOverflow = undefined;
      this._scrollLocked = false;
    }

    bindCarousel() {
      const c = this.refs.carousel;
      if (!c) return;
      let startX = null;
      let mDown = null;

      c.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      c.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (dx < -SWIPE_THRESHOLD) this.setCarouselIdx(this.state.carouselIdx + 1);
        else if (dx > SWIPE_THRESHOLD) this.setCarouselIdx(this.state.carouselIdx - 1);
        startX = null;
      });

      c.addEventListener('mousedown', (e) => { mDown = e.clientX; });
      c.addEventListener('mouseup', (e) => {
        if (mDown === null) return;
        const dx = e.clientX - mDown;
        if (dx < -SWIPE_THRESHOLD) this.setCarouselIdx(this.state.carouselIdx + 1);
        else if (dx > SWIPE_THRESHOLD) this.setCarouselIdx(this.state.carouselIdx - 1);
        mDown = null;
      });
    }

    buildDots() {
      const dots = this.refs.dots;
      if (!dots) return;
      dots.innerHTML = '';
      for (let i = 0; i < this.panelCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'd' + (i === 0 ? ' is-on' : '');
        dot.setAttribute('aria-label', `Go to panel ${i + 1}`);
        dot.style.cssText = 'border:0; padding:0; appearance:none; cursor:pointer; pointer-events:auto;';
        dot.addEventListener('click', () => this.setCarouselIdx(i));
        dots.appendChild(dot);
      }
    }

    /* Desktop preview strip. Panel 0 is a mini live compositor (mirrors the
       main scrubber + soaps build); panels 1+ are the lifestyle photo's src
       lifted from the rendered DOM. Hidden on mobile via CSS. */
    buildThumbs() {
      const wrap = this.refs.thumbs;
      if (!wrap || !this.refs.track) return;
      wrap.innerHTML = '';
      const panels = this.refs.track.querySelectorAll('.cr-panel');
      panels.forEach((panel, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cr-thumb' + (i === 0 ? ' is-on' : '');
        btn.setAttribute('aria-label', i === 0 ? 'Your set preview' : `Go to panel ${i + 1}`);
        btn.addEventListener('click', () => this.setCarouselIdx(i));

        if (i === 0) {
          btn.classList.add('cr-thumb--compositor');
          btn.innerHTML =
            '<img class="sbr-layer sbr-layer--left"     data-thumb-layer="left"     alt="" hidden>' +
            '<img class="sbr-layer sbr-layer--right"    data-thumb-layer="right"    alt="" hidden>' +
            '<img class="sbr-layer sbr-layer--scrubber" data-thumb-layer="scrubber" alt="" hidden>';
        } else {
          // Prefer the desktop image (1:1 source in the <picture>) — the
          // .cr-panel-photo's plain src is the mobile fallback (390x240).
          const picture       = panel.querySelector('picture');
          const desktopSource = picture && picture.querySelector('source[media*="min-width"]');
          let   url           = null;
          if (desktopSource) {
            const srcset = desktopSource.getAttribute('srcset') || '';
            const first  = srcset.split(',')[0].trim().split(/\s+/)[0];
            if (first) url = first;
          }
          const srcImg = panel.querySelector('.cr-panel-photo');
          if (!url) url = srcImg && (srcImg.currentSrc || srcImg.src);
          if (url) {
            const img = document.createElement('img');
            img.src = url;
            img.alt = (srcImg && srcImg.alt) || '';
            img.loading = 'lazy';
            btn.appendChild(img);
          }
        }
        wrap.appendChild(btn);
      });

      // Cache thumb compositor layer refs (just created).
      this.refs.thumbLayerLeft     = wrap.querySelector('[data-thumb-layer="left"]');
      this.refs.thumbLayerRight    = wrap.querySelector('[data-thumb-layer="right"]');
      this.refs.thumbLayerScrubber = wrap.querySelector('[data-thumb-layer="scrubber"]');

      this.bindThumbsArrows();
    }

    /* Horizontal scroll arrows for the desktop thumb strip. Each click
       advances by one thumb-width + gap. Arrows auto-hide when there's
       no overflow or when scrolled to the matching edge. */
    bindThumbsArrows() {
      const strip = this.refs.thumbs;
      const prev  = this.refs.thumbsArrowPrev;
      const next  = this.refs.thumbsArrowNext;
      if (!strip || (!prev && !next)) return;

      const step = () => {
        const first = strip.querySelector('.cr-thumb');
        if (!first) return strip.clientWidth * 0.6;
        const gap = parseFloat(getComputedStyle(strip).columnGap) || 10;
        return first.getBoundingClientRect().width + gap;
      };

      prev?.addEventListener('click', () => {
        strip.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      next?.addEventListener('click', () => {
        strip.scrollBy({ left: step(), behavior: 'smooth' });
      });

      strip.addEventListener('scroll', () => this.updateThumbsArrowVisibility(), { passive: true });
      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(() => this.updateThumbsArrowVisibility()).observe(strip);
      } else {
        window.addEventListener('resize', () => this.updateThumbsArrowVisibility());
      }
      // Initial pass once layout has settled.
      requestAnimationFrame(() => this.updateThumbsArrowVisibility());
    }

    updateThumbsArrowVisibility() {
      const strip = this.refs.thumbs;
      const prev  = this.refs.thumbsArrowPrev;
      const next  = this.refs.thumbsArrowNext;
      if (!strip) return;
      const hasOverflow = strip.scrollWidth > strip.clientWidth + 1;
      if (!hasOverflow) {
        prev?.classList.add('is-hidden');
        next?.classList.add('is-hidden');
        return;
      }
      const max = strip.scrollWidth - strip.clientWidth;
      prev?.classList.toggle('is-hidden', strip.scrollLeft <= 1);
      next?.classList.toggle('is-hidden', strip.scrollLeft >= max - 1);
    }

    observeSection() {
      // Once the sticky section enters the viewport, the page-level ATC bar
      // fades in and stays forever. Mirrors the prototype's onSectionVisible behavior.
      const sec = this.refs.stickySection;
      if (!sec || !this.refs.atcWrap) return;

      // If section is the very first thing on the page (above the fold), show immediately.
      const rect = sec.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9) {
        this.state.atcVisible = true;
        this.renderAtcVisibility();
      }

      if (typeof IntersectionObserver === 'undefined') {
        // Fallback: just always show
        this.state.atcVisible = true;
        this.renderAtcVisibility();
        return;
      }

      const io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.state.atcVisible) {
            this.state.atcVisible = true;
            this.renderAtcVisibility();
            io.disconnect();
            break;
          }
        }
      }, { threshold: 0.05, rootMargin: '0px 0px -10% 0px' });
      io.observe(sec);
    }

    /* ─── State setters ─────────────────────────────────────── */
    setScrubber(variantId) {
      this.state.scrubberVariantId = String(variantId);
      this.render();
    }

    incScent(variantId) {
      // Fill the first empty slot with this variant.
      const slots = this.state.slotOrder.slice();
      const empty = slots.indexOf(null);
      if (empty === -1) return;  // all full
      slots[empty] = String(variantId);
      this.state.slotOrder = slots;
      this.render();
    }

    decScent(variantId) {
      // Empty the LAST slot containing this variant.
      const slots = this.state.slotOrder.slice();
      let last = -1;
      for (let i = slots.length - 1; i >= 0; i--) {
        if (slots[i] === String(variantId)) { last = i; break; }
      }
      if (last === -1) return;
      slots[last] = null;
      this.state.slotOrder = slots;
      this.render();
    }

    setSlot(idx, variantId) {
      // Bucket picker: assign a specific slot to a specific variant.
      const slots = this.state.slotOrder.slice();
      slots[idx] = variantId == null ? null : String(variantId);
      this.state.slotOrder = slots;
      this.render();
    }

    countOf(variantId) {
      return this.state.slotOrder.filter(v => v === String(variantId)).length;
    }

    setPlan(plan) {
      if (plan !== 'subscribe' && plan !== 'once') return;
      this.state.plan = plan;
      this.render();
    }

    setCarouselIdx(idx) {
      this.state.carouselIdx = Math.max(0, Math.min(this.panelCount - 1, idx));
      this.renderCarousel();
    }

    /* ─── Helpers ───────────────────────────────────────────── */
    totalScents() {
      return this.state.slotOrder.filter(Boolean).length;
    }

    selectionOrder() {
      // Filtered slot list (no nulls), in slot order.
      return this.state.slotOrder.filter(Boolean);
    }

    findSoap(variantId) {
      return this.data.soaps.find(s => String(s.variantId) === String(variantId));
    }

    findScrubberVariant(id) {
      return this.data.scrubber.variants.find(v => String(v.id) === String(id));
    }

    findSkScrubberVariant(scrubberVariantId) {
      const scrubber = this.findScrubberVariant(scrubberVariantId);
      if (!scrubber) return null;
      return this.data.skScrubber.variants.find(v => v.title === scrubber.title);
    }

    isReady() {
      return !!this.state.scrubberVariantId
        && this.totalScents() === this.data.settings.requiredSoaps;
    }

    resolveCompositor() {
      const leftVid    = this.state.slotOrder[0] || null;
      const rightVid   = this.state.slotOrder[1] || null;
      const scrubberId = this.state.scrubberVariantId;
      const anySoap    = !!(leftVid || rightVid);
      const swap       = (this.data.settings && this.data.settings.useScrubberWSoap !== false);

      let scrubberSrc = '';
      if (scrubberId) {
        const v = this.findScrubberVariant(scrubberId);
        if (v) scrubberSrc = (anySoap && swap) ? (v.soloWithSoapImage || '') : (v.soloImage || '');
      }

      let leftSrc = '';
      if (leftVid) {
        const soap = this.findSoap(leftVid);
        if (soap) leftSrc = soap.packLeftImage || '';
      }

      let rightSrc = '';
      if (rightVid) {
        const soap = this.findSoap(rightVid);
        if (soap) rightSrc = soap.packRightImage || '';
      }

      return { scrubberSrc, leftSrc, rightSrc };
    }

    /* ─── Render ────────────────────────────────────────────── */
    render() {
      this.renderHero();
      this.renderColorRow();
      this.renderScentGrid();
      this.renderBucket();
      this.renderCounter();
      this.renderScentNotes();
      this.renderPlan();
      this.renderPrice();
      this.renderCompositor();
      this.renderAtc();
      this.renderCarousel();
    }

    renderHero() {
      // Drive the gradient backdrop from the selected scrubber's resolved hex
      // (metafield → color map → fallback). Mirror onto the pip so the
      // mini-preview keeps the same scrubber-color gradient as the main hero.
      const v = this.findScrubberVariant(this.state.scrubberVariantId);
      const hex = this.resolveSwatchHex(v);
      if (this.refs.rh)       this.refs.rh.style.setProperty('--scrub-hex', hex);
      if (this.refs.pipInner) this.refs.pipInner.style.setProperty('--scrub-hex', hex);
      // Desktop thumb compositor mirrors the same scrubber-tinted backdrop.
      const thumbComp = this.refs.thumbs?.querySelector('.cr-thumb--compositor');
      if (thumbComp) thumbComp.style.setProperty('--scrub-hex', hex);
    }

    renderColorRow() {
      const id = this.state.scrubberVariantId;
      this.refs.colorRow?.querySelectorAll('.swatch').forEach(btn => {
        const isActive = btn.getAttribute('data-variant-id') === id;
        btn.classList.toggle('is-on', isActive);
        btn.setAttribute('aria-checked', String(isActive));
      });
      if (this.refs.colorName) {
        const v = this.findScrubberVariant(id);
        this.refs.colorName.textContent = v ? v.title : '';
      }
    }

    renderScentGrid() {
      const required = this.data.settings.requiredSoaps;
      const total = this.totalScents();
      this.refs.scentGrid?.querySelectorAll('.scent-card-lg').forEach(card => {
        const vid = card.getAttribute('data-variant-id');
        const count = this.countOf(vid);
        const isOn = count > 0;
        const isFull = total >= required;
        const isDisabled = !isOn && isFull;

        card.classList.toggle('is-on', isOn);
        card.classList.toggle('is-disabled', isDisabled);

        const stepper = card.querySelector('.stepper');
        if (stepper) {
          stepper.hidden = !isOn;
          const countEl = stepper.querySelector('[data-sbr-stepper-count]');
          if (countEl) countEl.textContent = String(count);
          const inc = stepper.querySelector('[data-act="inc"]');
          const dec = stepper.querySelector('[data-act="dec"]');
          if (inc) inc.disabled = isFull;
          if (dec) dec.disabled = count <= 0;
        }
      });
    }

    renderBucket() {
      if (!this.refs.bucketRoot) return;
      this.refs.bucketRoot.querySelectorAll('[data-sbr-slot]').forEach(slotEl => {
        const idx = parseInt(slotEl.getAttribute('data-slot'), 10);
        const vid = this.state.slotOrder[idx];
        const empty = slotEl.querySelector('[data-slot-empty]');
        const filled = slotEl.querySelector('[data-slot-filled]');
        if (vid) {
          slotEl.classList.add('is-filled');
          if (empty)  empty.hidden = true;
          if (filled) filled.hidden = false;
          const soap = this.findSoap(vid);
          const nameEl = filled?.querySelector('[data-slot-name]');
          if (nameEl && soap) nameEl.textContent = soap.title;
          const dotEl = filled?.querySelector('[data-slot-dot]');
          if (dotEl && soap) {
            if (soap.thumb) {
              dotEl.innerHTML = `<img src="${soap.thumb}" alt="" loading="lazy">`;
              dotEl.style.background = '';
            } else {
              dotEl.innerHTML = '';
              if (soap.scentColor) dotEl.style.background = soap.scentColor;
            }
          }
        } else {
          slotEl.classList.remove('is-filled');
          if (empty)  empty.hidden = false;
          if (filled) filled.hidden = true;
        }
      });

      // If sheet is open, mark current scent in grid
      if (this.refs.bucketSheet) {
        const openSlot = this.state.openSlot;
        const currentVid = openSlot != null ? this.state.slotOrder[openSlot] : null;
        this.refs.bucketSheet.querySelectorAll('[data-sbr-bucket-scent]').forEach(btn => {
          const v = btn.getAttribute('data-variant-id');
          btn.classList.toggle('is-current', v === currentVid);
        });
      }
    }

    renderCounter() {
      if (!this.refs.counter) return;
      this.refs.counter.textContent = `${this.totalScents()}/${this.data.settings.requiredSoaps}`;
    }

    renderScentNotes() {
      if (!this.refs.scentNotes) return;
      // Aggregate slotOrder into [vid, count] pairs
      const counts = {};
      this.state.slotOrder.forEach(v => {
        if (v) counts[v] = (counts[v] || 0) + 1;
      });
      const ids = Object.entries(counts);
      if (ids.length === 0) {
        this.refs.scentNotes.innerHTML = '';
        return;
      }
      const html = ids.map(([vid, c]) => {
        const soap = this.findSoap(vid);
        if (!soap) return '';
        const dotStyle = soap.scentColor ? `background:${soap.scentColor};` : '';
        const qty = c > 1 ? ` · ×${c}` : '';
        const desc = soap.scentNotes ? `<div class="sbr-scent-note-desc">${this.escape(soap.scentNotes)}</div>` : '';
        return `
          <div class="sbr-scent-note-row">
            <span class="sbr-scent-note-dot" style="${dotStyle}"></span>
            <div style="flex:1;min-width:0;">
              <div class="sbr-scent-note-name">${this.escape(soap.title)}${qty}</div>
              ${desc}
            </div>
          </div>`;
      }).join('');
      this.refs.scentNotes.innerHTML = html;
    }

    escape(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, m => ({
        '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
      }[m]));
    }

    renderPlan() {
      const plan = this.state.plan;
      this.root.setAttribute('data-plan', plan);

      // Toggle .is-on / .is-collapsed on plan options across all 3 layouts.
      this.refs.plan?.querySelectorAll('[data-plan]').forEach(el => {
        const isActive = el.getAttribute('data-plan') === plan;
        el.classList.toggle('is-on', isActive);
        el.classList.toggle('is-collapsed', !isActive);
        el.setAttribute('aria-checked', String(isActive));
      });

      // Segmented layout: swap the detail panel between sub/once content.
      if (this.refs.planDetailSub)  this.refs.planDetailSub.hidden  = plan !== 'subscribe';
      if (this.refs.planDetailOnce) this.refs.planDetailOnce.hidden = plan !== 'once';
    }

    renderPrice() {
      const sub  = this.data.settings.priceSub;
      const once = this.data.settings.priceOnce;
      if (this.refs.priceNow)    this.refs.priceNow.textContent    = '$' + (this.state.plan === 'subscribe' ? sub : once);
      if (this.refs.priceStrike) this.refs.priceStrike.textContent = '$' + once;
    }

    renderCompositor() {
      const { scrubberSrc, leftSrc, rightSrc } = this.resolveCompositor();
      this.applyLayer(this.refs.layerLeft,        leftSrc);
      this.applyLayer(this.refs.layerRight,       rightSrc);
      this.applyLayer(this.refs.layerScrubber,    scrubberSrc);
      this.applyLayer(this.refs.pipLayerLeft,     leftSrc);
      this.applyLayer(this.refs.pipLayerRight,    rightSrc);
      this.applyLayer(this.refs.pipLayerScrubber, scrubberSrc);
      this.applyLayer(this.refs.thumbLayerLeft,     leftSrc);
      this.applyLayer(this.refs.thumbLayerRight,    rightSrc);
      this.applyLayer(this.refs.thumbLayerScrubber, scrubberSrc);
    }

    applyLayer(imgEl, src) {
      if (!imgEl) return;
      if (!src) {
        imgEl.removeAttribute('src');
        imgEl.hidden = true;
        return;
      }
      if (imgEl.getAttribute('src') !== src) imgEl.setAttribute('src', src);
      imgEl.hidden = false;
    }

    renderAtc() {
      const ready = this.isReady();
      const cta = this.refs.atcCta;
      if (!cta) return;

      cta.classList.toggle('is-pending', !ready && !this.state.submitting);
      cta.setAttribute('data-state', this.state.submitting ? 'loading' : (ready ? 'ready' : 'incomplete'));

      if (this.refs.atcLabel) {
        this.refs.atcLabel.textContent = this.state.submitting
          ? 'ADDING…'
          : (ready ? this.data.settings.atcLabelReady : this.data.settings.atcLabelIncomplete);
      }

      const sub = this.data.settings.priceSub;
      const once = this.data.settings.priceOnce;
      if (this.refs.atcPriceNow) {
        this.refs.atcPriceNow.textContent = '$' + (this.state.plan === 'subscribe' ? sub : once);
      }
      if (this.refs.atcPriceStrike) {
        if (this.state.plan === 'subscribe') {
          this.refs.atcPriceStrike.textContent = '$' + once;
          this.refs.atcPriceStrike.hidden = false;
        } else {
          this.refs.atcPriceStrike.textContent = '';
          this.refs.atcPriceStrike.hidden = true;
        }
      }
      if (this.refs.atcCaption) {
        this.refs.atcCaption.textContent = this.state.plan === 'subscribe'
          ? this.data.settings.atcSubCaption
          : this.data.settings.atcOnceCaption;
      }
    }

    renderAtcVisibility() {
      if (!this.refs.atcWrap) return;
      this.refs.atcWrap.classList.toggle('is-visible', this.state.atcVisible);
      this.refs.atcWrap.setAttribute('aria-hidden', String(!this.state.atcVisible));
    }

    renderCarousel() {
      const idx = this.state.carouselIdx;
      if (this.refs.track) {
        this.refs.track.style.transform = `translateX(-${idx * 100}%)`;
      }
      if (this.refs.carousel) {
        this.refs.carousel.setAttribute('data-on-photo', idx > 0 ? '1' : '0');
      }
      this.refs.dots?.querySelectorAll('.d').forEach((d, i) => {
        d.classList.toggle('is-on', i === idx);
      });
      this.refs.thumbs?.querySelectorAll('.cr-thumb').forEach((t, i) => {
        t.classList.toggle('is-on', i === idx);
      });
      if (this.refs.pip) {
        this.refs.pip.classList.toggle('is-visible', idx !== 0);
      }
      if (this.refs.arrowPrev) this.refs.arrowPrev.classList.toggle('is-hidden', idx === 0);
      if (this.refs.arrowNext) this.refs.arrowNext.classList.toggle('is-hidden', idx === this.panelCount - 1);
    }

    /* ─── Cart submission ───────────────────────────────────── */
    async handleAtc() {
      if (this.state.submitting) return;
      if (!this.isReady()) {
        // Scroll the soap-pick section into view, but offset down past the
        // sticky menu + sticky hero carousel so the heading isn't hidden
        // behind them.
        const scents = this.root.querySelector('#sbr-scents');
        if (scents) {
          const heroEl = this.refs.stickySection?.querySelector('.sbr-sticky-hero');
          const heroH = heroEl ? heroEl.getBoundingClientRect().height : 240;
          const headerH = parseInt(getComputedStyle(this.root).getPropertyValue('--sbr-header-offset')) || 60;
          const padding = 16;
          const targetY = scents.getBoundingClientRect().top + window.scrollY - heroH - headerH - padding;
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        }
        return;
      }

      this.state.submitting = true;
      this.renderAtc();

      try {
        const items = this.buildCartItems();
        if (window.SBR_DEBUG) console.log('[sbr] ATC payload', { mode: this.state.plan, items });
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          console.error('[sbr] /cart/add.js rejected', { status: res.status, body: errData, items });
          const err = new Error(errData.description || errData.message || ('Cart add failed (HTTP ' + res.status + ')'));
          err.code = 'SBR_CART_ADD_REJECTED';
          throw err;
        }

        // Discount handling — prefer v2's debounced helper if loaded (so v1/v2/v3
        // share the same applied-discount state and don't double-apply).
        if (this.state.plan === 'subscribe') {
          const codes = [this.data.settings.discountCode, this.data.settings.shippingDiscountCode];
          if (typeof window._skApplyDiscount === 'function') {
            await window._skApplyDiscount(codes);
          } else {
            await applyDiscounts(codes);
          }
        } else {
          await clearDiscounts();
        }

        // Refresh cart drawer + bubble. Suppress v2's auto re-inject during
        // the swap so it doesn't fight the new render, then explicitly call
        // _skInjectKitCard to group items into a kit card (mirroring v2's flow).
        if (typeof window._skSetSuppressed === 'function') window._skSetSuppressed(true);
        try {
          const sectionsRes = await fetch('/cart?sections=cart-drawer,cart-icon-bubble');
          const sections = await sectionsRes.json();
          const drawerEl = document.querySelector('cart-drawer');
          if (drawerEl && typeof drawerEl.renderContents === 'function') {
            drawerEl.classList.remove('is-empty');
            drawerEl.renderContents({ sections });
          } else {
            this.swapSection('cart-drawer', sections['cart-drawer']);
            this.swapSection('cart-icon-bubble', sections['cart-icon-bubble']);
          }
        } catch (e) { /* non-fatal */ }
        // Explicitly inject the kit card 250ms after render (per v2's pattern),
        // then release suppression. Without the explicit call, the MutationObserver
        // suppresses itself and the kit card never appears on the first add.
        setTimeout(() => {
          if (typeof window._skInjectKitCard === 'function') window._skInjectKitCard();
          setTimeout(() => {
            if (typeof window._skSetSuppressed === 'function') window._skSetSuppressed(false);
          }, 1000);
        }, 250);

        document.dispatchEvent(new CustomEvent('cart:open'));
        const cartDrawerEl = document.querySelector('cart-drawer');
        if (cartDrawerEl && typeof cartDrawerEl.open === 'function') cartDrawerEl.open();

      } catch (err) {
        console.error('[sbr] add to cart failed', err);
        // Typed config errors (no SK variant, no selling plan) carry a
        // user-friendly message — surface it directly. Anything else is a
        // network/Shopify failure and gets the generic copy.
        const userMsg = (err && err.code && typeof err.message === 'string')
          ? err.message
          : 'Sorry — something went wrong adding to cart. Please try again.';
        alert(userMsg);
      } finally {
        this.state.submitting = false;
        this.renderAtc();
      }
    }

    buildCartItems() {
      // Match v2's bundle-key convention so v2's Layer 1 cart-drawer kit-card
      // injector picks up our items and groups them as a single kit card.
      const bundleKeyPrefix = window._skBundleKeyPrefix || 'smoosh-starter-kit';
      const bundleKey = `${bundleKeyPrefix}-${Date.now()}`;
      const mode = this.state.plan === 'subscribe' ? 'subscribe' : 'onetime';
      const kitCard = (this.data && this.data.kitCard) || {};
      const scrubberKitProps = {
        '_kit_card_title':     kitCard.title    || '',
        '_kit_card_sub_badge': kitCard.subBadge || '',
        '_kit_card_refill':    kitCard.refill   || '',
        '_kit_card_remove':    kitCard.remove   || ''
      };
      const sourceTag = (this.data && this.data.settings && this.data.settings.sourceTag) || '';
      const bundleProps = (label) => {
        const props = {
          '_bundle_key':   bundleKey,
          '_bundle_label': label,
          '_bundle_mode':  mode
        };
        // Hidden order line-item property (underscore-prefixed properties are
        // never shown to the customer in cart/checkout) so orders placed from
        // this section instance can be traced back to which page it lived on.
        // Set per-instance via the theme editor "Attribution / tracking" setting.
        if (sourceTag) props['_source_tag'] = sourceTag;
        if (label === 'scrubber') Object.assign(props, scrubberKitProps);
        return props;
      };

      const items = [];
      const scrubberVid = this.state.scrubberVariantId;

      // Aggregate slot order into per-variant quantities
      const counts = {};
      this.state.slotOrder.forEach(v => {
        if (v) counts[v] = (counts[v] || 0) + 1;
      });

      if (mode === 'subscribe') {
        const skVariant = this.findSkScrubberVariant(scrubberVid);
        // Subscribe must resolve to an SK scrubber variant. If it doesn't,
        // we'd silently degrade to a one-time scrubber inside a "subscribe"
        // bundle — discounts would mismatch and the kit card would lie.
        if (!skVariant || !skVariant.id) {
          const e = new Error('Subscription scrubber unavailable for this color. Please choose another color or switch to one-time.');
          e.code = 'SBR_NO_SK_VARIANT';
          throw e;
        }
        items.push({
          id: skVariant.id,
          quantity: 1,
          properties: bundleProps('scrubber')
        });
        for (const [vid, qty] of Object.entries(counts)) {
          if (qty <= 0) continue;
          const soap = this.findSoap(vid);
          if (!soap) continue;
          // Prefer the SK soap variant + SK selling plan (the 0%-intro/15%-
          // recurring plan tuned for the kit promo). Fall back to the regular
          // variant + its default selling plan if the SK mapping is absent
          // (section not yet configured with sk_soap_collection).
          const useVariantId    = soap.skVariantId    || soap.variantId;
          const useSellingPlan  = soap.skSellingPlanId || soap.sellingPlanId;
          if (!useSellingPlan) {
            const e = new Error(`"${soap.title}" isn't available on subscription. Please pick a different scent.`);
            e.code = 'SBR_NO_SELLING_PLAN';
            throw e;
          }
          items.push({
            id: useVariantId,
            quantity: qty,
            selling_plan: useSellingPlan,
            properties: bundleProps('soap')
          });
        }
      } else {
        items.push({
          id: scrubberVid,
          quantity: 1,
          properties: bundleProps('scrubber')
        });
        for (const [vid, qty] of Object.entries(counts)) {
          if (qty <= 0) continue;
          const soap = this.findSoap(vid);
          if (!soap) continue;
          items.push({
            id: soap.variantId,
            quantity: qty,
            properties: bundleProps('soap')
          });
        }
      }
      return items;
    }

    swapSection(id, html) {
      if (!html) return;
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const incoming = tmp.querySelector(`#shopify-section-${id}`) || tmp.firstElementChild;
      const target = document.getElementById(`shopify-section-${id}`);
      if (target && incoming) target.innerHTML = incoming.innerHTML;
    }
  }

  /* ─── Boot ──────────────────────────────────────────────────── */
  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(root => {
      if (root._sbrInstance) return;
      root._sbrInstance = new SetBuilder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector(ROOT_SELECTOR);
    if (root && !root._sbrInstance) root._sbrInstance = new SetBuilder(root);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector(ROOT_SELECTOR);
    if (root && root._sbrInstance) {
      if (typeof root._sbrInstance.destroy === 'function') root._sbrInstance.destroy();
      root._sbrInstance = null;
    }
  });
})();
